import {
    queueContinueReadingClicked,
    queueDiscoveryCardClicked,
    trackDiscoveryRailShown,
    type AnalyticsDiscoveryTarget,
    type AnalyticsSurface,
} from "@/main/analytics/analyticsEvents";
import { recordFeedClickOrigin } from "@/main/analytics/feedClickOrigin";

/**
 * The one place a recommendation-rail click turns into measurement — the rails' counterpart to
 * `feedAttribution.ts`, and it exists for the same reason.
 *
 * <p>Two things have to happen on that click and they are useless apart: the GA4 event (rich
 * dimensions, blind to anonymous traffic, consent-decliners and ad-blockers) and the click-origin
 * handoff that becomes `discussion_views.source` (one low-cardinality token, covers everybody, joinable
 * against product data in SQL). Routing both through `onDiscoveryCardActivated` means no call site can
 * wire up half of it — which is precisely what happened to the rails between 2026-07-30 and 2026-08-07,
 * leaving `RELATED_RAIL` a declared label that nothing emitted while 99.6% of discussion views stayed
 * unattributed.
 *
 * <p>Design: designs/01-now/2026-08-07-recommendation-surface-measurement-design.md.
 */

/**
 * `surface` → the `discussion_views.source` token, for the surfaces whose cards lead to a discussion.
 *
 * <p>Only discussion targets get an entry: a group/series/publisher card navigates to a page that has
 * no `discussion_views` row to attribute, and `group_views` / `series_views` / `user_profile_views`
 * have no `source` column. Those surfaces are measured by the GA4 half alone.
 *
 * <p>The server validates against its own copy of this closed set (`DiscussionViewSource`) and coerces
 * anything else to `unknown`, so this map is a convenience, never a trust boundary.
 */
const SURFACE_TO_VIEW_SOURCE: Record<string, string> = {
    related: "related_rail",
    home_rail: "recently_viewed",
    mobile_inline: "recently_viewed",
};

export type DiscoveryCardAttribution = {
    targetType: AnalyticsDiscoveryTarget;
    surface: AnalyticsSurface;
    /** Present only when the card leads to a discussion; absent for group/series/publisher cards. */
    discussionPublicId?: string | null;
};

/**
 * Call from the card's own click handler — never from a `useEffect`, and never from a container's
 * `onClickCapture`, which would also fire for a vote or a bookmark toggle rendered inside the card.
 *
 * <p>Analytics is strictly side-effect-only: nothing here may throw into the navigation it rides on.
 */
export function onDiscoveryCardActivated(attribution: DiscoveryCardAttribution): void {
    try {
        queueDiscoveryCardClicked(attribution.targetType, attribution.surface);

        const source = SURFACE_TO_VIEW_SOURCE[String(attribution.surface)];
        if (source && attribution.discussionPublicId) {
            recordFeedClickOrigin({
                discussionPublicId: attribution.discussionPublicId,
                source,
            });
        }
    } catch {
        // deliberately swallowed — a measurement failure must never cost the user their click
    }
}

/**
 * The continue-reading rail's counterpart to {@link onDiscoveryCardActivated}. It keeps its own event
 * (`continue_reading_clicked` — a resume is not a recommendation) but needs both halves for the same
 * reason, and its durable token is `recently_viewed`, which has been a declared-but-unemitted label
 * since 2026-07-30.
 */
export function onContinueReadingActivated(
    surface: AnalyticsSurface,
    discussionPublicId: string | undefined | null,
): void {
    try {
        queueContinueReadingClicked(surface);

        const source = SURFACE_TO_VIEW_SOURCE[String(surface)];
        if (source && discussionPublicId) {
            recordFeedClickOrigin({ discussionPublicId, source });
        }
    } catch {
        // deliberately swallowed — a measurement failure must never cost the user their click
    }
}

/**
 * The `discovery_rail_shown` half. Callers must already have deduped — this fires whenever it is
 * called. Fire only for a module that is OPEN and RESOLVED with at least one item: a loading, errored,
 * collapsed or empty module is not an impression, and counting it makes an unfillable rail look merely
 * unpopular.
 */
export function reportDiscoveryRailShown(options: {
    targetType: AnalyticsDiscoveryTarget;
    surface: AnalyticsSurface;
    itemCount: number;
}): void {
    try {
        if (options.itemCount <= 0) {
            return;
        }

        trackDiscoveryRailShown(options);
    } catch {
        // side-effect-only
    }
}
